import { ProductCategory } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import {
  Button,
  CategorySelectorItem,
  CategorySelectorWrapper,
  DrawerCategoryFilters,
  hasActiveFilters,
} from '@components';
import styled from '@emotion/styled';
import { useAppDispatch, useAppSelector } from '@hooks';
import { useCategory } from '@hooks/category/category.hook';
import { Clear, Close } from '@mui/icons-material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Box, Drawer, Stack } from '@mui/material';
import { getModalState, setModalState } from '@store/modal/modal.slice';
import { GlobalStyles } from '@themes/global/GlobalStyles';
import { spacing } from '@themes/theme.helpers';
import { t } from '@translations';
import { useState } from 'react';

export type CategoryDrawerProps = {
  modalId: string;
  showFiltersAndPagination?: boolean;
  categories: ProductCategory[];
  handleCategoryChange: (category: ProductCategory) => void;
  isCategorySelected: (category: ProductCategory) => boolean;
  skipEmptyCategories?: boolean;
  isCategoryOpened: (category: ProductCategory) => boolean;
};

const DrawerContent = styled(Box)`
  min-width: 250px;
  position: relative;
  height: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const DrawerFooter = styled(Stack)`
  padding-bottom: ${spacing(2)};
  margin-top: ${spacing(2)};
`;

const DrawerHeader = styled(Box)`
  width: 100%;
  text-align: right;
  padding: ${spacing(2)};
  cursor: pointer;
`;

export const CategoryDrawer = ({
  modalId,
  showFiltersAndPagination,
  categories,
  handleCategoryChange,
  isCategorySelected,
  skipEmptyCategories,
  isCategoryOpened,
}: CategoryDrawerProps) => {
  const modalState = useAppSelector(getModalState(modalId));
  const selectedCategories = categories.filter(isCategoryOpened);
  const showClearButton = hasActiveFilters();

  const dispatch = useAppDispatch();
  const [showSelectedOnly, setShowSelectedOnly] = useState(true);
  const { handleClearFilters } = useCategory();

  const closeHandler = () => {
    setShowSelectedOnly(true);
    dispatch(setModalState({ id: modalId, isVisible: false }));
  };

  const closeModalOnCategoryChange = (category: ProductCategory) => {
    handleCategoryChange(category);
    closeHandler();
  };

  const toggleCategories = () => {
    setShowSelectedOnly(!showSelectedOnly);
  };

  return (
    <Drawer variant='temporary' open={modalState.isVisible} onClose={closeHandler}>
      <GlobalStyles>
        <DrawerContent>
          <DrawerHeader onClick={closeHandler}>
            <Close sx={{ fontSize: 30 }} />
          </DrawerHeader>
          <CategorySelectorWrapper>
            {showFiltersAndPagination && (
              <Button
                variant='text'
                size='small'
                onClick={toggleCategories}
                startIcon={showSelectedOnly ? <ArrowBackIosIcon /> : undefined}
                endIcon={showSelectedOnly ? undefined : <ArrowForwardIosIcon />}
              >
                {showSelectedOnly
                  ? t(TextCodes.Search.ShowAllCategories)
                  : t(TextCodes.Search.ShowSelectedCategory)}
              </Button>
            )}
            {showSelectedOnly && showFiltersAndPagination
              ? selectedCategories.map((category) => (
                  <CategorySelectorItem
                    key={category.categoryLink}
                    category={category}
                    onChange={closeModalOnCategoryChange}
                    isCategorySelected={isCategorySelected}
                    skipEmptyCategories={skipEmptyCategories}
                    isCategoryOpened={isCategoryOpened}
                    shouldClose={false}
                  />
                ))
              : categories.map((category) => (
                  <CategorySelectorItem
                    key={category.categoryLink}
                    category={category}
                    onChange={closeModalOnCategoryChange}
                    isCategorySelected={isCategorySelected}
                    skipEmptyCategories={skipEmptyCategories}
                    isCategoryOpened={isCategoryOpened}
                    shouldClose={showFiltersAndPagination}
                  />
                ))}
            {showFiltersAndPagination && <DrawerCategoryFilters />}
          </CategorySelectorWrapper>
          <DrawerFooter spacing={2} direction='row' justifyContent='center'>
            <Button variant='primary' onClick={closeHandler}>
              {t(TextCodes.Base.Close)}
            </Button>
            {showClearButton && showFiltersAndPagination ? (
              <Button
                variant='tertiary'
                onClick={() => {
                  handleClearFilters();
                  closeHandler();
                }}
                startIcon={<Clear />}
              >
                {t(TextCodes.Base.ClearFilters)}
              </Button>
            ) : null}
          </DrawerFooter>
        </DrawerContent>
      </GlobalStyles>
    </Drawer>
  );
};
